import {
  Badge,
  Box,
  CircularProgress,
  Divider,
  Flex,
  HStack,
  Img,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import {
  ASSESSMENTS,
  FileDBValue,
  getAssessmentFilesDir,
  getAssessmentName,
  getPatientFileField,
  getPatientFilesDir,
  getResizedFilePath,
  objectToArray,
  UpdateCallback,
  USERS,
} from '@hb/shared'
import { deleteField, doc, setDoc, updateDoc } from 'firebase/firestore'

import { addMetadata, PopUpMessageContext, ScreenContext, useApp, useAuth } from '@hb/react'

import { useCallback, useContext, useMemo } from 'react'
import { db } from '../../backend/db/db'
import { deleteFile } from '../../backend/storage/utils'
import { useMyAssessments } from '../../contexts/user/UserAssessmentsContext'
import uploadIcon from '../../icons/upload.svg'
import { SolidActionButton } from '../Buttons/ActionButton'
import { EditableFileView } from '../forms/Input'
import { SignInToView } from '../Users/SignInToView'
import { PageContainer } from '../Views/PageContainer'
import { UploadPatientFileModal } from './UploadPatientFileModal'

interface PatientFileData {
  file: FileDBValue
  pregnancyId: string | null
  pregnancyName: string | null
  baseStoragePath: string
}

export const PatientFilesView = () => {
  const { contentWidth } = useContext(ScreenContext)
  const { authUser, loading: authLoading, user } = useAuth()
  const { processResponse } = useContext(PopUpMessageContext)
  const { appName } = useApp()
  const { uid } = authUser ?? {}

  const { loading: assessmentsLoading, allAssessmentsArr } = useMyAssessments()

  const patientFiles = useMemo<Array<PatientFileData>>(() => {
    if (!uid) return []
    return objectToArray(user?.files ?? {})
      .map(file => ({
        file,
        pregnancyName: null,
        pregnancyId: null,
        baseStoragePath: `${getPatientFilesDir(uid, false, 'patient')}/${file.id}`,
      }))
      .sort((a, b) => {
        const aDate = a.file.uploadedOn ?? 0
        const bDate = b.file.uploadedOn ?? 0
        return bDate - aDate
      })
  }, [user, uid])

  const allFiles = useMemo<Array<PatientFileData>>(() => {
    const files: Array<PatientFileData> = []

    // Add patient files
    files.push(...patientFiles)

    // Add pregnancy files
    allAssessmentsArr.forEach(assessment => {
      const assessmentFiles = objectToArray(assessment.files ?? {}).map(file => ({
        file,
        pregnancyName: getAssessmentName(assessment),
        baseStoragePath: `${getAssessmentFilesDir(assessment.id, 'patient')}/${file.id}`,
        pregnancyId: assessment.id,
      }))
      files.push(...assessmentFiles)
    })

    // Sort all files by most recent first
    return files.sort((a, b) => {
      const aDate = a.file.uploadedOn ?? 0
      const bDate = b.file.uploadedOn ?? 0
      return bDate - aDate
    })
  }, [patientFiles, allAssessmentsArr])

  const { isOpen, onOpen, onClose } = useDisclosure()

  const handleDeleteFile = useCallback(
    async ({ file, pregnancyId }: PatientFileData): Promise<UpdateCallback> => {
      if (!uid) return { error: 'Not logged in' }
      const docRef = pregnancyId ? doc(db, ASSESSMENTS, pregnancyId) : doc(db, USERS, uid)
      if (file.storagePath) {
        const resizedFilePath = getResizedFilePath(file.storagePath)
        await deleteFile(resizedFilePath).catch(() => {})
        await deleteFile(file.storagePath).catch(() => {})
      }
      await updateDoc(docRef, `files.${file.id}`, deleteField())
      return { success: 'File deleted' }
    },
    [uid],
  )

  const handleUpdateFile = useCallback(
    async (existing: PatientFileData, data: any): Promise<UpdateCallback> => {
      if (!uid) return { error: 'Not logged in' }
      const docRef = existing.pregnancyId
        ? doc(db, ASSESSMENTS, existing.pregnancyId)
        : doc(db, USERS, uid)

      try {
        await setDoc(
          docRef,
          { files: { [existing.file.id]: addMetadata(data, appName, true) } },
          { merge: true },
        )
        return { success: 'File updated' }
      } catch (err: any) {
        console.error(err)
        return processResponse({
          error: err?.message || 'Error uploading file',
        })
      }
    },
    [processResponse, appName, uid],
  )

  const onSubmitExisting = useCallback(
    async (existing: PatientFileData, file: FileDBValue | null) =>
      file ? handleUpdateFile(existing, file) : handleDeleteFile(existing),
    [handleUpdateFile, handleDeleteFile],
  )

  if (!authUser) {
    return <SignInToView message="Sign in to upload files" />
  }

  const renderFileList = (files: Array<PatientFileData>) => (
    <VStack flex={1} minH="0" w="100%" divider={<Divider />} overflowY="auto">
      {files.length ? (
        files.map(data => (
          <EditableFileView
            key={data.file.id}
            baseStoragePath={data.baseStoragePath}
            onSubmit={file => onSubmitExisting(data, file)}
            header={
              data.pregnancyName ? (
                <Box>
                  <Badge colorScheme="green" fontSize="sm">
                    Pregnancy: {data.pregnancyName}
                  </Badge>
                </Box>
              ) : null
            }
            fieldPathSegments={[]}
            data={data.file}
            stackProps={{ opacity: data.file.uploadedOn ? 1 : 0.7 }}
            field={getPatientFileField(data.file.id)}
          />
        ))
      ) : (
        <Text fontFamily="Hero-New" fontSize="lg" color="gray.600" px={4} py={2}>
          No files uploaded yet
        </Text>
      )}
    </VStack>
  )

  const body =
    authLoading || assessmentsLoading ? (
      <HStack p={2} bg="white" borderRadius={6} boxShadow="md">
        <CircularProgress isIndeterminate />
        <Text>Loading...</Text>
      </HStack>
    ) : (
      <Flex
        flex={1}
        w="100%"
        height="100%"
        overflowY="auto"
        width={contentWidth}
        position="relative"
        direction="column"
        align="center">
        <VStack
          bg="white"
          alignItems="flex-start"
          borderRadius={6}
          spacing={0}
          boxShadow="md"
          maxH="100%"
          w="100%"
          maxW="800px">
          <Flex
            borderBottom="1px solid #cdcdcd"
            py={2}
            pl={4}
            pr={3}
            bg="gray.50"
            w="100%"
            justify="space-between"
            align="center">
            <Text flex={1} fontFamily="Hero-New" fontSize="xl" color="gray.600" fontWeight={500}>
              Patient Files
            </Text>
            <Box>
              <SolidActionButton
                alignItems="center"
                py={1.5}
                pl={3}
                pr={4}
                height="auto"
                size="sm"
                gap={1}
                onClick={onOpen}
                w="100%">
                <Img
                  src={uploadIcon}
                  height="22px"
                  filter="brightness(3) drop-shadow(0 0 2px rgba(0,0,0,0.5))"
                />
                <Text lineHeight={1} fontFamily="Hero-New" fontSize="md" fontWeight={500}>
                  UPLOAD FILE
                </Text>
              </SolidActionButton>
            </Box>
          </Flex>
          {renderFileList(allFiles)}
        </VStack>
      </Flex>
    )

  return (
    <PageContainer fullHeight>
      {body}
      <UploadPatientFileModal
        isOpen={isOpen}
        onClose={onClose}
        assessmentsArr={allAssessmentsArr}
      />
    </PageContainer>
  )
}
